import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { connect, type ConnectedProps } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ExternalLink,
  MapPin,
  MoreHorizontal,
  Pin,
  ShieldCheck,
} from 'lucide-react';

import { Badge as _Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

import DataService from '../../services/data-service';
import type { RootState } from '../../store';
import type { StashCardPresentation } from '../../store/stash-scoped/types';
import { useStashScopedStore } from '../../store/stash-scoped/context';
import { StashCardDetailStoreProvider } from '../../store/stash-card-detail/context';
import {
  useCategoryBreadcrumb,
  useFeaturedNote,
  useHistory,
  useStashCard,
  useStashersStrip,
  useVendor,
} from '../../store/stash-card-detail/hooks';

import { useAppShell } from '../app-shell';
import { ErrorInfo } from '../common/ErrorInfo';
import { VendorReach } from './VendorReach';
import { StashersStrip } from './StashersStrip';
import { HistoryTimeline } from './HistoryTimeline';
import { MapEmbed } from './MapEmbed';

import type {
  StashCard,
  Stasher,
  StashCategory,
} from '@stash/shared-types';

const Badge = _Badge as React.ComponentType<{
  variant?: string;
  className?: string;
  children?: React.ReactNode;
}>;

// ───── Redux glue ─────

const mapStateToProps = (state: RootState) => ({
  currentUser: state.auth.currentUser,
});

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface PageOuterProps extends PropsFromRedux {
  id?: string;
  presentation?: StashCardPresentation;
}

function buildCardFromPresentation(p: StashCardPresentation): StashCard {
  const meta = { createdAt: new Date(), updatedAt: new Date(), appVersion: 'demo' };
  return {
    recordID: p.id,
    stasherId: '',
    vendor: {
      recordID: p.vendorId,
      companyName: p.displayName,
      categories: [],
      stashCardStasherIds: [],
      importNotes: '',
      recordSource: 'iOSApp',
      meta,
    },
    tags: p.tags,
    recordStatus: 'active',
    visibility: 'communityPrivate',
    meta,
    stashCardActivities: [],
    stashCardNotes: p.note ? [{
      recordID: 'from-presentation',
      stashCardId: p.id,
      title: '',
      detail: p.note,
      isPrivate: false,
      attachments: [],
      createdOn: new Date(),
    }] : [],
  };
}

function StashCardPage({ currentUser, ...props }: PageOuterProps): React.JSX.Element {
  const params = useParams<{ id?: string }>();
  const location = useLocation();
  const cardId = props.id ?? params.id ?? '';

  // Optional presentation snapshot passed via navigate state (from StashBoard).
  const presentation =
    props.presentation ??
    (location.state as { presentation?: StashCardPresentation } | null)?.presentation;

  // ── StashScopedStore lookup (lifted provider survives route changes) ──────
  // Finds the full StashCard by ID from the shared store, avoiding a round-trip
  // for cards already in scope. Falls back to DataService only when the store is
  // fully loaded but the card genuinely isn't present (deep link, out-of-scope).
  const cardFromStore = useStashScopedStore(
    s => s.allStashCards.find(c => c.recordID === cardId) ?? null,
  );
  const stashersFromStore = useStashScopedStore(s => s.stashers);
  const categoriesFromStore = useStashScopedStore(s => s.categories);
  const isStoreLoaded = useStashScopedStore(s => s.isFirstLoadComplete);

  // Presentation stub — an instant lightweight card when navigating from StashBoard.
  // Used only when neither the store nor a previous fetch has the card.
  const presentationCard = useMemo(
    () => (presentation ? buildCardFromPresentation(presentation) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [presentation?.id],
  );

  // ── DataService fallback state ────────────────────────────────────────────
  const [fetchedCard, setFetchedCard] = useState<StashCard | null>(null);
  const [fetchedStashers, setFetchedStashers] = useState<Stasher[]>([]);
  const [fetchedCategories, setFetchedCategories] = useState<StashCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    // Already resolved from store — no fetch needed.
    if (!cardId || cardFromStore) return;
    // Store hasn't finished loading yet — wait for isStoreLoaded to flip.
    if (!isStoreLoaded) return;
    // Store is loaded but card is not in scope — fall back to DataService.
    const dataService = new DataService();
    setLoading(true);
    dataService
      .getStashCard(cardId)
      .then(async (fetched: StashCard) => {
        setFetchedCard(fetched);
        const fetchedStashersData: Stasher[] = await (dataService as unknown as {
          getStashersForVendor?: (id: string) => Promise<Stasher[]>;
        }).getStashersForVendor?.(fetched.vendor.recordID) ?? [];
        setFetchedStashers(fetchedStashersData);
        const fetchedCategoriesData: StashCategory[] = await (dataService as unknown as {
          getCategories?: () => Promise<StashCategory[]>;
        }).getCategories?.() ?? [];
        setFetchedCategories(fetchedCategoriesData);
      })
      .catch(setError)
      .finally(() => setLoading(false));
  }, [cardId, cardFromStore, isStoreLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Resolution priority: store > fetched > presentation stub ─────────────
  const activeCard = cardFromStore ?? fetchedCard ?? presentationCard;
  const activeStashers = cardFromStore ? stashersFromStore : fetchedStashers;
  const activeCategories = cardFromStore ? categoriesFromStore : fetchedCategories;

  // Waiting: no card available yet and data is still in-flight.
  if (!activeCard && (!isStoreLoaded || loading)) return <PageShell><LoadingState /></PageShell>;
  if (error && !activeCard) return <PageShell><ErrorInfo error={error} subject="this Stash Card" /></PageShell>;
  if (!activeCard) return <PageShell><LoadingState /></PageShell>;

  return (
    <StashCardDetailStoreProvider
      card={activeCard}
      stashers={activeStashers}
      categories={activeCategories}
      currentUserStasherId={currentUser?.userId ?? activeCard.stasherId}
    >
      <StashCardPageBody
        {...(presentation ? { presentation } : {})}
        currentUserId={currentUser?.userId ?? activeCard.stasherId}
      />
    </StashCardDetailStoreProvider>
  );
}

export default connector(StashCardPage);

// ───── Page chrome ─────

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-3xl px-4 py-8 md:px-6 md:py-12">
      {children}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="rounded-3xl border border-border bg-card p-10 text-center">
      <div className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
      <p className="mt-3 text-sm text-muted-foreground">Loading Stash Card…</p>
    </div>
  );
}

// ───── Body — runs inside the detail store provider ─────

interface StashCardPageBodyProps {
  presentation?: StashCardPresentation;
  currentUserId: string;
}

function StashCardPageBody({ presentation, currentUserId: _currentUserId }: StashCardPageBodyProps): React.JSX.Element {
  const card = useStashCard();
  const vendor = useVendor();
  const breadcrumb = useCategoryBreadcrumb();
  const featuredNote = useFeaturedNote();
  const history = useHistory();

  const friendIds = useMemo(() => new Set<string>(), []);
  const stashers = useStashersStrip(friendIds);

  const navigate = useNavigate();
  const { setBreadcrumb, setTopBarActions, setTopBarBack } = useAppShell();
  const vendorName = vendor?.companyName ?? 'Vendor';

  // Category segments for the breadcrumb. Built from the loaded card's category
  // chain (useCategoryBreadcrumb already picks the first of multiple categories
  // and walks its ancestors), so it works on a fresh URL load / refresh. Falls
  // back to the presentation snapshot only while the store is still resolving.
  const categorySegments = useMemo(() => {
    if (breadcrumb.length > 0) return breadcrumb;
    if (presentation?.categoryPath) {
      return presentation.categoryPath.split(/\s*[›>]\s*/).filter(Boolean);
    }
    return [];
  }, [breadcrumb, presentation?.categoryPath]);
  const categoryLabel = categorySegments.join(' › ');

  useEffect(() => {
    if (!vendor) return;
    setBreadcrumb([...categorySegments, vendorName]);
    setTopBarBack(() => navigate(-1));
    setTopBarActions(
      <div className="flex items-center gap-1">
        <Button variant="outline" size="icon-sm" aria-label="Pin"><Pin size={14} /></Button>
        <Button variant="outline" size="icon-sm" aria-label="More"><MoreHorizontal size={14} /></Button>
      </div>,
    );
    return () => { setBreadcrumb([]); setTopBarActions(null); setTopBarBack(null); };
  }, [setBreadcrumb, setTopBarActions, setTopBarBack, navigate, vendorName, categorySegments, vendor]);

  if (!card || !vendor) return <PageShell><LoadingState /></PageShell>;

  const homeBase = presentation?.stashedBy?.name ? presentation.stashedBy.name : 'Stash';

  return (
    <div className="mx-auto max-w-3xl px-4 pb-24 pt-6 md:px-6 md:pt-8">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={card.recordID}
            initial={{ y: '-2%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '-2%', opacity: 0 }}
            transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
            className="flex flex-col gap-7"
          >

            {/* ───── The Card ───── */}
            <article
              data-screen-label="StashCard"
              className="overflow-hidden rounded-3xl border border-border bg-card shadow-sm"
            >
              {/* Header band */}
              <div className="bg-gradient-to-b from-[var(--stash-blue-light)]/40 to-transparent px-6 pt-5 pb-1 md:px-7">
                
                <div className="mt-5 text-center">
                  {homeBase && (
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {homeBase}
                    </p>
                  )}
                  <h1 className="mt-1.5 text-3xl font-bold tracking-tight md:text-4xl">
                    {vendorName}
                  </h1>
                  {vendor.companyDescription && (
                    <p className="mx-auto mt-2 max-w-prose text-sm text-muted-foreground">
                      {vendor.companyDescription}
                    </p>
                  )}

                  <div className="flex items-center justify-center my-2">
                    {categoryLabel && (
                      <Badge variant="secondary" className="rounded-full">
                        {categoryLabel}
                      </Badge>
                    )}
                  </div>

                  <div className="mt-3 inline-flex flex-wrap items-center justify-center gap-2">
                    {vendor.address && (
                      <Badge variant="secondary" className="rounded-full">
                        <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[var(--stash-green)]" />
                        Open · 24/7
                      </Badge>
                    )}
                    <Badge variant="secondary" className="rounded-full">
                      <span className="mr-1 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[var(--stash-blue)] text-background">
                        <ShieldCheck size={9} strokeWidth={3} />
                      </span>
                      Verified · {vendor.stashCardStasherIds?.length ?? 0} Stashers
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Reach bar */}
              <div className="px-4 pt-5 md:px-5">
                <VendorReach />
              </div>

              {/* Address row */}
              {vendor.address && (
                <div className="flex items-center gap-3 px-6 pt-5 pb-4 md:px-7">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[var(--stash-blue)]/10 text-[var(--stash-blue)]">
                    <MapPin size={17} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-foreground">
                      {vendor.address.street}{vendor.address.street2 ? `, ${vendor.address.street2}` : ''}
                    </p>
                    <p className="truncate text-xs font-medium text-muted-foreground">
                      {vendor.address.city}, {vendor.address.region} {vendor.address.postalCode}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm">
                    Directions
                    <ExternalLink size={14} className="ml-1" />
                  </Button>
                </div>
              )}

              {/* Map embed */}
              {vendor.address && (
                <MapEmbed
                  {...(vendor.address.lat !== undefined ? { lat: vendor.address.lat } : {})}
                  {...(vendor.address.lng !== undefined ? { lng: vendor.address.lng } : {})}
                  address={[vendor.address.street, vendor.address.city, vendor.address.region, vendor.address.postalCode].filter(Boolean).join(', ')}
                  label={`${vendor.address.city}, ${vendor.address.region}`}
                  height={180}
                  className="rounded-none border-x-0"
                />
              )}

              {/* Stashers strip */}
              <div className="px-6 py-5 md:px-7">
                <StashersStrip count={vendor.stashCardStasherIds?.length ?? 0} chips={stashers} />
              </div>

              {/* Featured note */}
              {featuredNote && (
                <div className="px-4 pb-5 md:px-5">
                  <div className="flex gap-3 rounded-2xl bg-accent/30 p-4">
                    <Avatar className="mt-0.5 h-9 w-9">
                      {featuredNote.authorAvatarUrl && <AvatarImage src={featuredNote.authorAvatarUrl} />}
                      <AvatarFallback className="bg-accent text-accent-foreground text-xs font-bold">
                        {featuredNote.authorName.split(' ').map(s => s[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm italic leading-relaxed text-foreground">
                        &ldquo;{featuredNote.body}&rdquo;
                      </p>
                      <p className="mt-2 text-xs font-medium text-muted-foreground">
                        {featuredNote.authorName} · {featuredNote.ageLabel}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </article>

            {/* ───── History timeline ───── */}
            <HistoryTimeline vendorName={vendorName} items={history} />

          </motion.div>
        </AnimatePresence>
    </div>
  );
}
